
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { HashRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import DataInputView from './views/DataInputView';
import ResearcherView from './views/ResearcherView';
import ResearchFormView from './views/ResearchFormView';
import ImportSurveyView from './views/ImportSurveyView';
import ResearchAnalyticView from './views/ResearchAnalyticView';
import ResearchRewardView from './views/ResearchRewardView';
import SurveyResponsesView from './views/SurveyResponsesView';
import SurveyDashboardView from './views/SurveyDashboardView';
import LivePresentView from './views/LivePresentView';
import LiveAudienceView from './views/LiveAudienceView';
import DevicesView from './views/DevicesView';
import DataMarketView from './views/DataMarketView';
import ApiKeysView from './views/ApiKeysView';
import AdminAgentsView from './views/AdminAgentsView';
import SurveyResponseResultView from './views/SurveyResponseResultView';
import SurveyView from './views/SurveyView';
import SurveyLoader from './views/SurveyLoader';
import LoginView from './views/LoginView';
import RoleSelectionView from './views/RoleSelectionView';
import SchemaView from './views/SchemaView';
import SmartContractView from './views/SmartContractView';
import AdminView from './views/AdminView';
import CategoryView from './views/CategoryView';
import DataSourceView from './views/DataSourceView';
import PublicDataView from './views/PublicDataView';
import SettingsView from './views/SettingsView';
import PublicDashboardView from './views/PublicDashboardView';
import PrivacyVaultView from './views/PrivacyVaultView';
import MysteryBoxView from './views/MysteryBoxView';
import SiloSetupView from './views/SiloSetupView';
import SiloDirectoryView from './views/SiloDirectoryView';
import LandingView from './views/LandingView';
import { UserRole } from './types';
import { supabase } from './services/supabaseClient';
import { VaultProvider } from './services/zkVault/VaultContext';
import { Loader2, LogOut, AlertTriangle, RefreshCw } from 'lucide-react';

// Layout component for protected routes that require a specific role
const ProtectedLayout = ({ session, userRole }: { session: any, userRole: UserRole | null }) => {
  const location = useLocation();

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // If we have a session but no role, redirect to selection
  if (!userRole) {
    return <Navigate to="/select-role" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200">
      <Navbar userRole={userRole} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24 lg:pb-8">
        {/* Key forces remount on path change, satisfying "reload data on nav click" requirement */}
        <Outlet key={location.pathname} />
      </main>

      <footer className="border-t border-slate-800 mt-12 py-8 text-center text-slate-600 text-sm mb-16 md:mb-0">
        <p className="font-semibold text-slate-500 mb-1 uppercase tracking-wide">TINANEY - DataFi Platform</p>
        <p>&copy; 2026 E-KHMER Technology Co., Ltd</p>
      </footer>
    </div>
  );
};

// Component to redirect users to their primary workspace based on role
const RoleBasedRedirect = ({ userRole }: { userRole: UserRole }) => {
  switch (userRole) {
    case UserRole.GENERAL_USER:
      return <Navigate to="/earn/campaigns" replace />;
    case UserRole.RESEARCHER:
      return <Navigate to="/research/campaigns" replace />;
    case UserRole.CTO:
      return <Navigate to="/admin/data-points" replace />; // Default CTO view
    case UserRole.DATA_AGENT:
    case UserRole.AI_AGENT:
    default:
      return <Navigate to="/data" replace />;
  }
};

const AppContent: React.FC = () => {
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [session, setSession] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false); // Local loading for retry button
  const [showEscapeHatch, setShowEscapeHatch] = useState(false);

  // Refs to track state in closures without forcing re-renders of effects
  const isFetchingRole = useRef(false);
  const isLoadingRef = useRef(isLoading);
  const userRoleRef = useRef(userRole);

  // Keep refs in sync
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    userRoleRef.current = userRole;
  }, [userRole]);

  // Helper to fetch role from DB
  const fetchUserRole = useCallback(async (user: any) => {
    const userId = user.id;
    const userEmail = user.email;

    if (!userId) {
        setIsLoading(false);
        return;
    }

    if (isFetchingRole.current) return;
    isFetchingRole.current = true;

    try {
      // 1. SUPER ADMIN CHECK
      if (userEmail === 'sengtha@gmail.com') {
        console.log("Super Admin detected. Enforcing CTO role.");
        setUserRole(UserRole.CTO);
        const { error: adminError } = await supabase
          .from('users')
          .upsert({
            id: userId,
            email: userEmail,
            role: UserRole.CTO,
            username: user.user_metadata?.full_name || 'Super Admin'
          });
        if (adminError) console.error("Error enforcing admin role:", adminError);
      } else {
        // 2. STANDARD USER CHECK
        const { data, error } = await supabase
            .from('users')
            .select('role')
            .eq('id', userId)
            .maybeSingle();

        if (error) {
            console.warn("Error fetching role from DB (Network might be unstable):", error.message);
            // Don't reset role on network error, keep existing if any
        } else {
            if (data && data.role) {
                setUserRole(data.role as UserRole);
            } else {
                setUserRole(null);
            }
        }
      }
    } catch (e) {
      console.error("Unexpected error fetching role:", e);
    } finally {
      isFetchingRole.current = false;
      setIsLoading(false); // Ensure loader is dismissed - SOURCE OF TRUTH
    }
  }, []);

  // Function to refresh state (passed to RoleSelection)
  const refreshSession = useCallback(async (forcedRole?: UserRole) => {
    if (forcedRole) {
        console.log("Applying forced role update:", forcedRole);
        setUserRole(forcedRole);

        // Optimistic update of session
        const { data: { session: newSession } } = await supabase.auth.getSession();
        setSession(newSession);
        return;
    }

    setIsRetrying(true); // Show local loading on the button
    setShowEscapeHatch(false);

    try {
      const { data: { session: newSession }, error } = await supabase.auth.getSession();

      if (error || !newSession) {
          throw error || new Error("No session retrieved");
      }

      setSession(newSession);
      if (newSession?.user) {
        await fetchUserRole(newSession.user);
      } else {
        setUserRole(null);
        setIsLoading(false);
      }
    } catch (err) {
      console.error("Refresh session error:", err);
      // Don't kill the app, just stop loading to show potentially cached UI or login
      setIsLoading(false);
    } finally {
      setIsRetrying(false);
    }
  }, [fetchUserRole]);

  // Robust Visibility Listener - Fixes infinite loops on wake
  useEffect(() => {
    const handleVisibilityChange = async () => {
        if (document.visibilityState === 'visible') {
            console.log("Tab active: Checking session validity...");
            try {
                // Force check session validity with server
                const { data, error } = await supabase.auth.getSession();

                if (error || !data.session) {
                    console.warn("Session invalid on wake. Clearing state.");
                    setSession(null);
                    setUserRole(null);
                    setIsLoading(false);
                } else {
                    // Session is valid.
                    if (!session || session.access_token !== data.session.access_token) {
                        console.log("Syncing valid session...");
                        setSession(data.session);
                        // Fetch role only if missing to avoid unnecessary calls
                        if (!userRoleRef.current) {
                            await fetchUserRole(data.session.user);
                        }
                    }
                    // Safety: Ensure loading is off
                    setIsLoading(false);
                }
            } catch (e) {
                console.error("Wake check failed:", e);
                setIsLoading(false);
            }
        }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [session, fetchUserRole]);

  // Main Auth Subscription
  useEffect(() => {
    let mounted = true;

    // Safety Timeout: If stuck loading for > 5s, try to recover
    const safetyTimer = setTimeout(async () => {
        // Use Ref to check current loading state to avoid stale closure issues
        if (mounted && isLoadingRef.current) {
            console.warn("Loading timeout reached (5s). Unblocking UI.");
            setIsLoading(false);
            setShowEscapeHatch(true);
        }
    }, 5000);

    const initializeApp = async () => {
        try {
            // 1. Get initial session
            const { data: { session: initialSession }, error } = await supabase.auth.getSession();

            if (error) throw error;

            if (mounted) {
                setSession(initialSession);
                if (initialSession?.user) {
                    await fetchUserRole(initialSession.user);
                } else {
                    // No user, stop loading
                    setIsLoading(false);
                }
            }
        } catch (err) {
            console.error("Initialization error:", err);
            if (mounted) setIsLoading(false);
        }
    };

    initializeApp();

    // 2. Set up listener
    // FIX: Make callback fully synchronous and defer work to avoid blocking auth thread
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
        if (!mounted) return;

        // Defer all state updates/async work to next tick
        setTimeout(async () => {
            console.log("Auth Event (Async):", event);
            setSession(newSession);

            if (event === 'INITIAL_SESSION') {
                return;
            }

            if (event === 'SIGNED_IN') {
                if (newSession?.user) {
                    await fetchUserRole(newSession.user);
                } else {
                    setIsLoading(false);
                }
            } else if (event === 'TOKEN_REFRESHED') {
                // Check if we have role via ref to avoid stale closures or unnecessary fetching
                if (newSession?.user && !userRoleRef.current) {
                    await fetchUserRole(newSession.user);
                } else {
                    // IMPORTANT: Stop loading even if we didn't fetch role
                    setIsLoading(false);
                }
            } else if (event === 'SIGNED_OUT') {
                setUserRole(null);
                setSession(null);
                setIsLoading(false);
            } else {
                // All other events (PASSWORD_RECOVERY etc), stop loading
                setIsLoading(false);
            }
        }, 0);
    });

    return () => {
      mounted = false;
      clearTimeout(safetyTimer);
      subscription.unsubscribe();
    };
  }, [fetchUserRole]); // Removed userRole from deps, rely on userRoleRef

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center space-y-6">
        <Loader2 className="animate-spin text-indigo-500" size={48} />
        <p className="text-slate-500 text-sm animate-pulse">Connecting to secure storage...</p>

        {showEscapeHatch && (
            <div className="flex flex-col items-center gap-3 animate-in fade-in slide-in-from-bottom-4 px-6 text-center">
                <div className="text-yellow-500 text-xs flex items-center gap-2 bg-yellow-900/20 px-3 py-2 rounded-lg border border-yellow-900/50">
                    <AlertTriangle size={14} /> Connection slow?
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => refreshSession()}
                        disabled={isRetrying}
                        className="flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300 bg-indigo-900/20 hover:bg-indigo-900/30 px-4 py-2 rounded-lg transition-colors border border-indigo-900/50 disabled:opacity-50"
                    >
                        {isRetrying ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                        Retry
                    </button>
                    <button
                        onClick={async () => {
                            await supabase.auth.signOut();
                            window.location.href = '/';
                        }}
                        className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300 bg-red-900/20 hover:bg-red-900/30 px-4 py-2 rounded-lg transition-colors border border-red-900/50"
                    >
                        <LogOut size={14} /> Sign Out
                    </button>
                </div>
            </div>
        )}
      </div>
    );
  }

  return (
    <VaultProvider session={session}>
    <Routes>
      {/* PUBLIC ROUTES */}
      <Route path="/" element={ session && userRole ? <RoleBasedRedirect userRole={userRole}/> : <LandingView /> } />

      <Route path="/login" element={!session ? <LoginView /> : <Navigate to="/" replace />} />
      <Route path="/schema" element={<SchemaView />} />
      <Route path="/smart-contract" element={<SmartContractView />} />
      <Route path="/survey/:id" element={<SurveyLoader />} />
      <Route path="/live/:id" element={<LiveAudienceView />} />

      <Route
        path="/select-role"
        element={
          session && !userRole
            ? <RoleSelectionView onRoleSelected={refreshSession} />
            : <Navigate to="/" replace />
        }
      />

      {/* PROTECTED ROUTES */}
      <Route element={<ProtectedLayout session={session} userRole={userRole} />}>
        <Route path="/dashboard" element={<Navigate to="/" replace />} /> {/* Redirect old dashboard links */}

        <Route path="/data" element={<DataInputView />} />

        {/* Researcher Routes */}
        <Route path="/research/campaigns" element={<ResearcherView />} />
        <Route path="/research/campaigns/:id/responses" element={<SurveyResponsesView />} />
        <Route path="/research/campaigns/:id/dashboard" element={<SurveyDashboardView />} />
        <Route path="/research/campaigns/:id/present" element={<LivePresentView />} />
        <Route path="/iot/devices" element={<DevicesView />} />
        <Route path="/iot/market" element={<DataMarketView />} />
        <Route path="/research/responses/:responseId" element={<SurveyResponseResultView />} />
        <Route path="/research/create" element={<ResearchFormView />} />
        <Route path="/research/import" element={<ImportSurveyView />} />
        <Route path="/research/analytics" element={<ResearchAnalyticView />} />
        <Route path="/research/gift" element={<ResearchRewardView />} />
        <Route path="/research/silos" element={<SiloSetupView />} />

        {/* Silo Surveys (BYOI) — discover + take surveys hosted on researchers' own silos */}
        <Route path="/silos" element={<SiloDirectoryView />} />

        {/* General User Routes */}
        <Route path="/earn/campaigns" element={<SurveyView view="campaigns" />} />
        <Route path="/earn/gifts" element={<SurveyView view="gifts" />} />
        <Route path="/earn/mystery" element={<MysteryBoxView />} />

        {/* Admin/CTO Routes */}
        <Route path="/admin" element={userRole === UserRole.CTO ? <AdminView /> : <Navigate to="/" replace />} />
        <Route path="/admin/agents" element={userRole === UserRole.CTO ? <AdminAgentsView /> : <Navigate to="/" replace />} />
        <Route path="/admin/categories" element={userRole === UserRole.CTO ? <CategoryView /> : <Navigate to="/" replace />} />
        <Route path="/admin/sources" element={userRole === UserRole.CTO ? <DataSourceView /> : <Navigate to="/" replace />} />

        {/* Updated Route: Allow DATA_AGENT to view Public Ledger */}
        <Route
            path="/admin/data-points"
            element={
                (userRole === UserRole.CTO || userRole === UserRole.DATA_AGENT)
                ? <PublicDataView />
                : <Navigate to="/" replace />
            }
        />

        {/* Deprecated redirects */}
        <Route path="/research" element={<Navigate to="/research/campaigns" replace />} />
        <Route path="/earn" element={<Navigate to="/earn/campaigns" replace />} />

        <Route path="/privacy-vault" element={<PrivacyVaultView />} />
        <Route path="/settings" element={<SettingsView userRole={userRole} />} />
        <Route path="/settings/api" element={<ApiKeysView />} />
      </Route>

      {/* Catch all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </VaultProvider>
  );
};

const App: React.FC = () => {
  return (
    <HashRouter>
      <AppContent />
    </HashRouter>
  );
};

export default App;
